import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { withTheme } from 'styled-components';

import { Heading } from '@techstyle/react-components';
import { Feature } from '@techstyle/react-features';
import { FormattedMessage } from '@techstyle/react-intl';

import { CheckoutFeatures } from '../../constants/checkout';
import { desktop, mobile } from '../../styles';
import { getIsCartFeatureEnabled } from '../../utils/cart';
import { parseDate } from '../../utils/dates';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { getCreditsApplied } from '../../utils/selectors';
import { Component as AfterpayMessaging } from '../AfterpayMessaging';
import { usePricingSummary } from '../CartDrawer/usePricingSummary';
import { Component as Price } from '../ExtendedPrice';
import KlarnaMessaging from '../KlarnaMessaging/KlarnaMessaging';
import { Component as PricePreviewContext } from '../PricePreviewContext';
import { Component as QuestionMarkToolTip } from '../QuestionMarkToolTip';

const sevenDays = 1000 * 60 * 60 * 24 * 7; // Seven days in milliseconds

const StyledQuestionMarkToolTip = styled(QuestionMarkToolTip)`
  position: absolute;
  top: -9px;
  z-index: ${props => props.theme.zIndex.increment};
  ${desktop`
    top: 4px;
  `}
`;

const Wrapper = styled.div`
  ${props =>
    (props.theme.context.checkout || props.theme.context.orders) &&
    desktop`
      font-size: 12px;
  `};

  .afterpay-paragraph {
    margin-top: 4px;
  }

  .afterpay-link {
    ${props => props.shouldHideAfterpayInfoIcon && `display: none !important`}
  }

  ${mobile`
    ${props =>
      props.theme.context.cartDrawer &&
      `padding: 0 ${props.theme.spacing.small}px;`}
  `}
  ${props =>
    props.theme.context.cartDrawer &&
    `
      .afterpay-paragraph {
        text-align: center;
        margin-top: 10px;

        .afterpay-logo, .afterpay-link {
          vertical-align: sub !important;
        }
      }
  `};
`;

const Items = styled.div``;

const ItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  ${props =>
    props.theme.context.orders &&
    mobile`
      margin-top: ${props => props.theme.spacing.tiny}px;
    `};

  ${props =>
    props.theme.context.cartDrawer &&
    mobile`
      margin-bottom: ${props => props.theme.spacing.tiny / 2}px;
    `};
`;

const TotalItem = styled(ItemWrapper)`
  margin-top: ${props => props.theme.spacing.tiny}px;
  padding-top: ${props => props.theme.spacing.tiny}px;
  border-top: 2px solid ${props => props.theme.colors.default};

  ${desktop`
    margin-top: ${props => props.theme.spacing.small}px;
    padding-top: ${props => props.theme.spacing.small}px;

      margin-top: ${props => props.theme.spacing.tiny}px;
      padding-top: ${props => props.theme.spacing.tiny}px;

  `}

  ${props =>
    props.theme.context.checkout &&
    desktop`
    font-size: 14px;
  `};

  ${props =>
    props.theme.context.orders &&
    mobile`
      font-size: ${props => props.theme.spacing.small}px;
    `};

  ${props =>
    props.theme.context.cartDrawer &&
    mobile`
      margin-top: 12px;
      border-top: 1px solid #CCCCCC;
    `};
`;

const SubHeader = styled.div`
  ${props => props.theme.subHeader.variants.subHeading4Uppercase.textStyles}
  color: ${props => props.theme.colors.default};

  font-weight: ${props => (props.bold ? 'bold' : 'normal')};
  position: relative;

  ${({ theme, total }) =>
    total
      ? theme.subHeader.variants.subHeading4Uppercase.textStyles
      : theme.paragraph.variants.paragraph4Uppercase.textStyles}
`;

const Title = styled(Heading).attrs({
  level: 3,
})`
  margin-bottom: ${props => props.theme.spacing.small}px;
  font-size: 14px;
  font-weight: bold;
  text-align: center;
  text-transform: uppercase;
  ${mobile`display: none`};
`;

const ShippingAndTaxesDisclaimer = styled.div`
  margin-top: ${props => props.theme.spacing.small}px;
  width: 100%;
  text-align: center;
  color: ${props => props.theme.colors.subdued};

  ${({ theme }) => theme.paragraph.variants.paragraph5Uppercase.textStyles}
  color: ${props => props.theme.colors.grey};
`;

const afterpayClassName = 'afterpay-price-total';

const VatInclusionText = styled.span`
  text-transform: none;
  font-weight: 400;
`;

class TotalPriceSummary extends Component {
  static propTypes = {
    afterpayExcludedProductType: PropTypes.string,
    creditsApplied: PropTypes.number,
    className: PropTypes.string,
    discounts: PropTypes.array,
    giftCardCredits: PropTypes.array,
    gwpPromo: PropTypes.object,
    gwpProduct: PropTypes.object,
    hasCustomizableItems: PropTypes.bool,
    hasPreorderItems: PropTypes.bool,
    hideDiscounts: PropTypes.bool,
    hideStoreCredits: PropTypes.bool,
    hideTariff: PropTypes.bool,
    hideTotal: PropTypes.bool,
    hideVAT: PropTypes.bool,
    isBorderfreeCustomer: PropTypes.bool,
    isDowngradedMember: PropTypes.bool,
    isNMP: PropTypes.bool,
    isOrderPage: PropTypes.bool,
    isEmpVip: PropTypes.bool,
    isEU: PropTypes.bool,
    isFromCart: PropTypes.bool,
    isGwpApplied: PropTypes.bool,
    isInfluencer: PropTypes.bool,
    isLead: PropTypes.bool,
    isPaygo: PropTypes.bool,
    isUS: PropTypes.bool,
    membershipItem: PropTypes.object,
    membershipPrice: PropTypes.number,
    order: PropTypes.object.isRequired,
    parentWrapperRef: PropTypes.object,
    registrationDate: PropTypes.string,
    retailDeliveryFee: PropTypes.number,
    isKlarnaEnabled: PropTypes.bool,
    /**
     * If false, the `shipping` and `tax` line items won't show. Only the
     * subtotal with discounts.
     */
    showFinalTotal: PropTypes.bool,
    showTax: PropTypes.oneOf(['always', 'never', 'nonzero']),
    shouldShowAfterpay: PropTypes.bool,
    storeCredits: PropTypes.object,
    tokenToCashEnabled: PropTypes.bool,
    theme: PropTypes.object,
    title: PropTypes.node,
    tld: PropTypes.string,
    useStoreCredit: PropTypes.bool,
    orderDiscounts: PropTypes.array,
    showPriceLoading: PropTypes.bool,
    tariffRate: PropTypes.string,
  };

  static defaultProps = {
    hideDiscounts: false,
    hideStoreCredits: false,
    hideTariff: false,
    hideTotal: false,
    hideVAT: true,
    isEU: false,
    isFromCart: false,
    membershipPrice: 0,
    showFinalTotal: true,
    showTax: 'always',
    shouldShowAfterpay: false,
  };

  shouldShowTax(previewTotal) {
    const { isEU, order, showTax, showFinalTotal } = this.props;

    if (previewTotal !== null && showTax !== 'always') {
      return false;
    }

    if (!showFinalTotal) {
      return false;
    }
    if (isEU) {
      return false;
    }
    switch (showTax) {
      case 'never':
        return false;
      case 'nonzero':
        return Boolean(order.tax);
      default:
        return true;
    }
  }

  shouldShowShipping(previewShipping) {
    if (previewShipping != null) {
      return true;
    }

    const { order, showFinalTotal } = this.props;

    if (!showFinalTotal) {
      return false;
    }
    if (order.shippingOptionId) {
      return true;
    }
    return false;
  }

  getMinusSign = () => {
    return this.props.showPriceLoading ? '' : '-';
  };

  renderDiscountList = () => {
    const { discounts, orderDiscounts, gwpPromo, showPriceLoading } =
      this.props;
    const discountList = orderDiscounts.length > 0 ? orderDiscounts : discounts;

    if (!discountList) {
      return;
    }

    return discountList.map(discount => {
      // Hide Gift With Purchase
      const isGwpProduct = discount.promoCode === gwpPromo?.promoCode;
      if (isGwpProduct) {
        return null;
      }

      return (
        <ItemWrapper key={discount.promoId}>
          <SubHeader special>
            <FormattedMessage
              id="global_checkout.promo_label"
              defaultMessage="Promo: {label}"
              values={{
                label: discount.promoLabel || discount.label,
              }}
            />
          </SubHeader>
          <SubHeader special>
            {this.getMinusSign()}
            <Price showLoading={showPriceLoading} amount={discount.amount} />
          </SubHeader>
        </ItemWrapper>
      );
    });
  };

  renderTariffLabel = () => {
    if (this.props.tariffRate) {
      return (
        <FormattedMessage
          id="global_checkout.tariff_test_887"
          defaultMessage="Please be advised that tariffs on fashion goods continue to apply and make products more expensive, including those imported from China. While many retailers raised their prices to offset the increased cost, we’ve added a {tariff}% surcharge to your purchase and are covering the remainder of the cost ourselves to minimize impact to our customers. We appreciate your understanding and patience as we work towards offering you the best value possible."
          values={{
            tariff: this.props.tariffRate,
          }}
        />
      );
    }
    return (
      <Feature handle="tariff_ab_test">
        {feature => {
          const shouldShowBiggerTariff =
            feature.data && feature.data.enabled && feature.data.value;
          return shouldShowBiggerTariff ? (
            <FormattedMessage
              id="global_checkout.tariff_explainer_525"
              defaultMessage="On September 1st, 2019, tariffs were increased on all fashion goods coming from China, making them 5.25% more expensive. We appreciate your understanding and patience as we work towards offering you the best value possible."
            />
          ) : (
            <FormattedMessage
              id="global_checkout.tariff_explainer_425"
              defaultMessage="On September 1st, 2019, tariffs were increased on all fashion goods coming from China, making them 4.25% more expensive. We appreciate your understanding and patience as we work towards offering you the best value possible."
            />
          );
        }}
      </Feature>
    );
  };

  render() {
    const {
      afterpayExcludedProductType,
      className,
      giftCardCredits,
      gwpProduct,
      hasCustomizableItems,
      hasPreorderItems,
      hideDiscounts,
      hideStoreCredits,
      hideTariff,
      hideTotal,
      hideVAT,
      isBorderfreeCustomer,
      isDowngradedMember,
      isEU,
      isEmpVip,
      isOrderPage,
      isFromCart,
      isFromCartDrawer,
      isGwpApplied,
      isInfluencer,
      isLead,
      isPaygo,
      isUS,
      membershipItem,
      onlyReturnTotalPrice,
      order,
      parentWrapperRef,
      registrationDate,
      retailDeliveryFee,
      shouldShowAfterpay,
      showFinalTotal,
      storeCredits,
      subTotalFromProps,
      theme: { context },
      title,
      tld,
      showPriceLoading,
      isKlarnaEnabled,
    } = this.props;

    const { features = [] } = order;

    const isRetailPriceTestEnabled = getIsCartFeatureEnabled(
      { features },
      CheckoutFeatures.RETAIL_PRICE_TEST
    );

    const shouldEnableVipSaving =
      (isLead || isPaygo || isDowngradedMember) &&
      !isInfluencer &&
      !isRetailPriceTestEnabled;

    let shouldShowVipSaving = false;
    if (shouldEnableVipSaving) {
      const registrationStartDate = parseDate(registrationDate);

      const hasBeenRegisteredForAWeek =
        Date.now() <= registrationStartDate.getTime() + sevenDays;

      shouldShowVipSaving = hasBeenRegisteredForAWeek;
    }

    // use order discount for orders
    const {
      useStoreCredit,
      useMembershipStoreCredit,
      membershipStoreCredit,
      storeCreditsOnly,
    } = storeCredits;
    const { memberTokenAmount, nonVipSubtotal, vipDiscount } = order;
    const tokenQuantity = order.tokenQuantity || order.memberTokenQuantity || 0;
    const items = order.items || order.orderLines;
    const showStoreCredits = useStoreCredit && !hideStoreCredits;
    const showMembershipStoreCredits =
      useMembershipStoreCredit && !hideStoreCredits;
    const showTariff =
      !hideTariff && parseFloat(order.tariffSurchargeAmount, 10) && isUS;
    const showEMPCredits = !!tokenQuantity;
    const empCreditsVipValue = items
      .filter(item => item.tokenQtyApplied || item.tokensApplied)
      .reduce(
        (total, item) =>
          total + (isEmpVip ? item.vipUnitPrice : item.purchaseUnitPrice),
        0
      );

    const vatFormattedMessage = (
      <FormattedMessage
        id="global_checkout.vat_included"
        defaultMessage="VAT Included"
      />
    );

    return (
      <PricePreviewContext.Consumer>
        {({ shipping: previewShipping, total: previewTotal }) => {
          const shouldShowShippingTaxesDisclaimer = isUS && context.cart;
          const showTax = this.shouldShowTax(previewTotal);
          const showShipping = this.shouldShowShipping(previewShipping);
          const shippingPrice =
            previewShipping == null ? order.shipping : previewShipping;
          let subTotalCart = order.subtotal;

          if (hideDiscounts) {
            if (showStoreCredits || showMembershipStoreCredits) {
              subTotalCart = order.subtotalMinusAllSavings;
            } else {
              subTotalCart = order.subtotalMinusDiscount;
            }
          } else if (isGwpApplied && gwpProduct) {
            // We don't want the GWP factored into the subtotal at all,
            // but there's no way to hide it via API - so we handle it here.
            subTotalCart -= gwpProduct.purchaseUnitPrice;
          }

          let subTotalMiniCart = previewTotal;
          let subTotalTax = order.tax;
          const tariffAmount = showTariff ? order.tariffSurchargeAmount : 0;

          if (previewTotal == null) {
            if (showFinalTotal) {
              subTotalMiniCart = order.total;
            } else {
              if (showStoreCredits || showMembershipStoreCredits) {
                subTotalMiniCart = order.subtotalMinusAllSavings + tariffAmount;
              } else {
                subTotalMiniCart = order.subtotalMinusDiscount + tariffAmount;
              }

              if (showEMPCredits) {
                subTotalMiniCart -= empCreditsVipValue;
              }

              // If retailDeliveryFee is available, we should include it in the calculation.
              // This usually only happens on cart where we do not calculate everything yet
              // (or use order.total)
              if (isFromCart && retailDeliveryFee) {
                subTotalMiniCart += retailDeliveryFee;
              }

              if (!isOrderPage) {
                giftCardCredits.forEach(({ creditAmount }) => {
                  subTotalMiniCart = Math.max(
                    subTotalMiniCart - creditAmount,
                    0
                  );
                });
              }
            }
          }

          const isMondialRelayApplicableInRegion =
            tld === '.fr' || tld === '.es';
          if (isMondialRelayApplicableInRegion && order.total && !isFromCart) {
            subTotalMiniCart = order.total;
          }

          if (subTotalMiniCart < 0) {
            subTotalMiniCart = previewTotal;
          }
          // while the order variable is still not filled because BE call is not ready
          // Some of its attributes will be undefined, leading to sums that end up in NaN
          // This check makes sure that Price components using
          // subTotalMiniCart will not get NaN as parameters that might break the App
          if (isNaN(subTotalMiniCart)) {
            subTotalMiniCart = 0;
          }

          // If the placed order has the deliveryTax key, THEN we deduct the tax
          // with the deliveryTax or retailDeliveryFee (they are the same).
          // Because it is already combined in the API. deliveryTax is only available on orders
          // that are already placed.
          if (showFinalTotal && order.deliveryTax && retailDeliveryFee) {
            subTotalTax -= retailDeliveryFee;
          }

          // Only used on the CollapsableFooter on mobile. The idea behind this is to avoid
          // calculating the price on the CartDrawer separately, to avoid any possible mismatches
          if (onlyReturnTotalPrice) {
            const minTotal = Math.min(subTotalFromProps, order.total);
            return <Price showLoading={showPriceLoading} amount={minTotal} />;
          }
          // TODO: Once shopping bag revamp goes live, we can remove the !isFromCartDrawer from the conditions below
          return (
            <Wrapper
              className={className}
              shouldHideAfterpayInfoIcon={
                hasPreorderItems || hasCustomizableItems
              }
            >
              {title ? <Title>{title}</Title> : null}
              <Items>
                <ItemWrapper data-qa-automation="orderSummaryRows">
                  <SubHeader bold={hideTotal}>
                    <FormattedMessage
                      id="global_checkout.subtotal"
                      defaultMessage="Subtotal"
                    />
                  </SubHeader>
                  <SubHeader bold={hideTotal}>
                    <Price
                      showLoading={showPriceLoading}
                      amount={
                        shouldShowVipSaving && membershipItem
                          ? nonVipSubtotal
                          : subTotalCart
                      }
                      className="bfx-total-subtotal"
                    />
                  </SubHeader>
                </ItemWrapper>
                {isFromCartDrawer && (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.savings"
                        defaultMessage="Savings"
                      />
                    </SubHeader>
                    <SubHeader>
                      <Price showLoading={showPriceLoading} amount={0} />
                    </SubHeader>
                  </ItemWrapper>
                )}
                {isFromCartDrawer && (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.shipping"
                        defaultMessage="Shipping"
                      />
                    </SubHeader>
                    <SubHeader>
                      <Price showLoading={showPriceLoading} amount={0} />
                    </SubHeader>
                  </ItemWrapper>
                )}
                {isFromCartDrawer && (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.tax"
                        defaultMessage="Tax"
                      />
                    </SubHeader>
                    <SubHeader>
                      <Price showLoading={showPriceLoading} amount={0} />
                    </SubHeader>
                  </ItemWrapper>
                )}
                {!isFromCartDrawer &&
                shouldShowVipSaving &&
                vipDiscount > 0 &&
                membershipItem ? (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader special>
                      <FormattedMessage
                        id="global_checkout.vip_discount_label"
                        defaultMessage="Total VIP Savings:"
                      />
                    </SubHeader>
                    <SubHeader special>
                      {this.getMinusSign()}
                      <Price
                        showLoading={showPriceLoading}
                        amount={vipDiscount}
                      />
                    </SubHeader>
                  </ItemWrapper>
                ) : null}
                {!isFromCartDrawer && !hideDiscounts && order.subtotalDiscount
                  ? this.renderDiscountList()
                  : null}
                {showStoreCredits ? (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.store_credit"
                        defaultMessage="Store credit"
                      />
                    </SubHeader>
                    <SubHeader>
                      {this.getMinusSign()}
                      <Price
                        showLoading={showPriceLoading}
                        amount={storeCreditsOnly}
                      />
                    </SubHeader>
                  </ItemWrapper>
                ) : null}

                {!isFromCartDrawer && !isOrderPage && giftCardCredits
                  ? giftCardCredits.map(cert => {
                      return (
                        <ItemWrapper
                          key={cert.creditId}
                          data-qa-automation="orderSummaryRows"
                        >
                          <SubHeader>
                            <FormattedMessage
                              id="global_checkout.gift_cert"
                              defaultMessage="Gift Card"
                            />
                            &nbsp;
                            {cert.creditCode}
                          </SubHeader>
                          <SubHeader>
                            {this.getMinusSign()}
                            <Price
                              showLoading={showPriceLoading}
                              amount={cert.creditAmount}
                            />
                          </SubHeader>
                        </ItemWrapper>
                      );
                    })
                  : null}

                {!isFromCartDrawer &&
                isOrderPage &&
                order.giftCertificateTotal ? (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.gift_cert"
                        defaultMessage="Gift Card"
                      />
                    </SubHeader>
                    <SubHeader>
                      {this.getMinusSign()}
                      <Price
                        showLoading={showPriceLoading}
                        amount={order.giftCertificateTotal}
                      />
                    </SubHeader>
                  </ItemWrapper>
                ) : null}
                {!isFromCartDrawer &&
                this.props.isNMP &&
                this.props.tokenToCashEnabled &&
                this.props.creditsApplied ? (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader
                      special
                      data-autotag="#credit_applied"
                      data-qa-automation="orderPriceCreditLoc"
                    >
                      {/** Fix this */}
                      <FormattedMessage
                        id="global_checkout.emp_credits_line_item"
                        defaultMessage="{credits} Credit(s) Applied"
                        values={{
                          credits: this.props.creditsApplied,
                        }}
                      />
                    </SubHeader>
                    <SubHeader
                      special
                      data-autotag="credit_amount"
                      data-qa-automation="orderPriceCreditAmountLoc"
                    >
                      {this.getMinusSign()}
                      <Price
                        showLoading={showPriceLoading}
                        amount={membershipStoreCredit}
                      />
                    </SubHeader>
                  </ItemWrapper>
                ) : (
                  <>
                    {!isFromCartDrawer && showMembershipStoreCredits ? (
                      <ItemWrapper data-qa-automation="orderSummaryRows">
                        <SubHeader>
                          <FormattedMessage
                            id="global_checkout.membership_credit"
                            defaultMessage="Member Credits"
                          />
                        </SubHeader>
                        <SubHeader>
                          {this.getMinusSign()}
                          <Price
                            showLoading={showPriceLoading}
                            amount={membershipStoreCredit}
                          />
                        </SubHeader>
                      </ItemWrapper>
                    ) : null}
                    {!isFromCartDrawer && showEMPCredits ? (
                      <ItemWrapper data-qa-automation="orderSummaryRows">
                        <SubHeader
                          special
                          data-autotag="#credit_applied"
                          data-qa-automation="orderPriceCreditLoc"
                        >
                          <FormattedMessage
                            id="global_checkout.emp_credits_line_item"
                            defaultMessage="{credits} Credit(s) Applied"
                            values={{
                              credits: tokenQuantity,
                            }}
                          />
                        </SubHeader>
                        <SubHeader
                          special
                          data-autotag="credit_amount"
                          data-qa-automation="orderPriceCreditAmountLoc"
                        >
                          {this.getMinusSign()}
                          <Price
                            showLoading={showPriceLoading}
                            amount={memberTokenAmount || empCreditsVipValue}
                          />
                        </SubHeader>
                      </ItemWrapper>
                    ) : null}
                  </>
                )}

                {!isFromCartDrawer && showShipping ? (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.shipping"
                        defaultMessage="Shipping"
                      />
                    </SubHeader>
                    <SubHeader>
                      {shippingPrice === 0 ? (
                        <FormattedMessage
                          id="global_checkout.free"
                          defaultMessage="Free"
                        />
                      ) : (
                        <Price
                          showLoading={showPriceLoading}
                          amount={shippingPrice}
                        />
                      )}
                    </SubHeader>
                  </ItemWrapper>
                ) : null}
                {showTariff ? (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.tariff"
                        defaultMessage="Tariff Tax"
                      />
                      <StyledQuestionMarkToolTip
                        anchorToRef={parentWrapperRef}
                        mobileTitle={
                          <FormattedMessage
                            id="global_checkout.tariff_header"
                            defaultMessage="Tariff Tax"
                          />
                        }
                      >
                        {this.renderTariffLabel()}
                      </StyledQuestionMarkToolTip>
                    </SubHeader>
                    <SubHeader>
                      <Price
                        showLoading={showPriceLoading}
                        amount={order.tariffSurchargeAmount}
                      />
                    </SubHeader>
                  </ItemWrapper>
                ) : null}
                {!isFromCartDrawer && showTax && (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader>
                      <FormattedMessage
                        id="global_checkout.tax"
                        defaultMessage="Tax"
                      />
                    </SubHeader>
                    <SubHeader data-qa-automation="orderTaxPriceText">
                      <Price
                        showLoading={showPriceLoading}
                        amount={subTotalTax}
                      />
                    </SubHeader>
                  </ItemWrapper>
                )}
                {retailDeliveryFee ? (
                  <ItemWrapper data-qa-automation="orderSummaryRows">
                    <SubHeader data-qa-automation="orderConfirmRetailDeliveryFeeLoc">
                      <FormattedMessage
                        id="global_checkout.retail_delivery_fee"
                        defaultMessage="Retail Delivery Fee"
                        data-qa-automation="historyRetailDeliveryFeeText"
                      />
                      <StyledQuestionMarkToolTip
                        data-qa-automation="orderRetailDeliveryTooltip"
                        anchorToRef={parentWrapperRef}
                        mobileTitle={
                          <FormattedMessage
                            id="global_checkout.retail_delivery_fee"
                            defaultMessage="Retail Delivery Fee"
                          />
                        }
                      >
                        <FormattedMessage
                          id="global_checkout.retail_delivery_fee_tool_tip"
                          defaultMessage="Effective July 1, 2022, Colorado imposes a non-refundable retail delivery fee of $0.27 on all deliveries by motor vehicle to a location in Colorado with at least one item of tangible personal property subject to state sales or use tax."
                        />
                      </StyledQuestionMarkToolTip>
                    </SubHeader>
                    <SubHeader>
                      <Price
                        showLoading={showPriceLoading}
                        amount={retailDeliveryFee}
                      />
                    </SubHeader>
                  </ItemWrapper>
                ) : null}
              </Items>
              {!hideTotal && !isBorderfreeCustomer && (
                <TotalItem
                  className={shouldShowAfterpay ? afterpayClassName : ''}
                  data-qa-automation="orderTotalField"
                >
                  <SubHeader bold uppercase total>
                    <>
                      <FormattedMessage
                        id="global_checkout.total"
                        defaultMessage="Total"
                      />
                      {shouldShowShippingTaxesDisclaimer ? '*' : null}
                      {isEU ? (
                        <VatInclusionText>
                          {' '}
                          {vatFormattedMessage}
                        </VatInclusionText>
                      ) : null}
                    </>
                  </SubHeader>
                  <SubHeader total bold>
                    <Price
                      showLoading={showPriceLoading}
                      amount={!isNaN(subTotalMiniCart) ? subTotalMiniCart : 0}
                      className={context.cardDrawer ? 'bfx-minicart-total' : ''}
                    />
                  </SubHeader>
                </TotalItem>
              )}
              {shouldShowAfterpay && !isKlarnaEnabled && (
                <AfterpayMessaging
                  afterpayClassName={afterpayClassName}
                  afterpayAmount={subTotalMiniCart}
                  excludedProducts={afterpayExcludedProductType}
                />
              )}
              {isKlarnaEnabled && (
                <KlarnaMessaging
                  baseAmount={!isNaN(subTotalMiniCart) ? subTotalMiniCart : 0}
                />
              )}
              {!isFromCartDrawer && !hideVAT && (
                <ItemWrapper>
                  <SubHeader>{vatFormattedMessage}</SubHeader>
                  <SubHeader>
                    <Price amount={order.vatAmount} />
                  </SubHeader>
                </ItemWrapper>
              )}
              {!isFromCartDrawer && shouldShowShippingTaxesDisclaimer ? (
                <ShippingAndTaxesDisclaimer>
                  <FormattedMessage
                    id="global_checkout.shipping_and_taxes_excluded"
                    defaultMessage="*Shipping &amp; taxes will be calculated at checkout"
                  />
                </ShippingAndTaxesDisclaimer>
              ) : null}
            </Wrapper>
          );
        }}
      </PricePreviewContext.Consumer>
    );
  }
}

const TotalPriceSummaryWithHooks = props => {
  const creditsApplied = useSelector(getCreditsApplied) || 0;

  const {
    'token-to-cash': tokenToCashEnabled,
    'additional-payment-methods': additionalPaymentMethods,
  } = useLDFlags();
  const isKlarnaEnabled =
    additionalPaymentMethods === 'klarna' ||
    additionalPaymentMethods === 'both';

  const { subtotal } = usePricingSummary();

  let tariffRate =
    (props?.order?.tariffSurchargeAmount ?? 0) /
    (props?.order?.subtotal - props?.order?.subtotalDiscount);

  switch (Math.floor(tariffRate * 100)) {
    case 2:
      tariffRate = '2';
      break;
    case 4:
      tariffRate = '4';
      break;
    case 5:
      tariffRate = '5.25';
      break;
    case 6:
      tariffRate = '6.25';
      break;
    case 8:
      tariffRate = '8.75';
      break;
    case 10:
      tariffRate = '10';
      break;
    case 11:
    case 12:
      if (tariffRate * 100 < 11.5) {
        tariffRate = '';
        break;
      }
      tariffRate = '12';
      break;
    default:
      tariffRate = '';
  }

  return (
    <TotalPriceSummary
      creditsApplied={creditsApplied}
      tokenToCashEnabled={tokenToCashEnabled}
      isKlarnaEnabled={isKlarnaEnabled}
      subTotalFromProps={subtotal}
      tariffRate={tariffRate}
      {...props}
    />
  );
};

export default withTheme(TotalPriceSummaryWithHooks);
